import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { IconInfoCircle } from '@tabler/icons-react';
import AuthLayout from './AuthLayout';
import OtpStep from './OtpStep';
import useAuthRedirect from './useAuthRedirect';
import {
  Button, Checkbox, PasswordField, PhoneField, TextField,
} from '../../components/common';
import authApi from '../../api/auth/authApi';
import { PATHS } from '../../routes/paths';
import { DEFAULT_COUNTRY_CODE } from '../../utils/constants';
import { formatICInput } from '../../utils/format';
import * as v from '../../utils/validators';

export default function RegisterPage() {
  const location = useLocation();
  const { complete } = useAuthRedirect();
  const [step, setStep] = useState('details');
  const [form, setForm] = useState({
    name: '', countryCode: DEFAULT_COUNTRY_CODE, phone: '', icNumber: '', email: '', password: '', agree: false,
  });
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [hint, setHint] = useState('');
  const [loading, setLoading] = useState(false);
  const set = (k) => (val) => setForm((f) => ({ ...f, [k]: val }));

  const requestCode = async () => {
    const res = await authApi.requestOtp({ countryCode: form.countryCode, phone: form.phone, purpose: 'register' });
    setHint(res.hint);
  };

  const submitDetails = async (e) => {
    e.preventDefault();
    const errs = v.validate(form, {
      name: (x) => v.required(x, 'Full name'),
      phone: v.phone,
      icNumber: v.ic,
      email: v.email,
      password: v.password,
      agree: (x) => (x ? '' : 'Please accept the terms to continue'),
    });
    setErrors(errs);
    setFormError('');
    if (Object.keys(errs).length) return;
    setLoading(true);
    try {
      await requestCode();
      setStep('code');
    } catch (ex) {
      setFormError(ex.message);
    }
    setLoading(false);
  };

  const verify = async (code) => {
    setLoading(true);
    setFormError('');
    try {
      const session = await authApi.register({
        name: form.name.trim(),
        countryCode: form.countryCode,
        phone: form.phone,
        icNumber: form.icNumber.replace(/\D/g, ''),
        email: form.email.trim(),
        password: form.password,
        code,
      });
      complete(session, true);
    } catch (ex) {
      setFormError(ex.message);
      setLoading(false);
    }
  };

  if (step === 'code') {
    return (
      <AuthLayout title="Verify your number" onBack={() => setStep('details')}>
        <OtpStep
          countryCode={form.countryCode}
          phone={form.phone}
          hint={hint}
          error={formError}
          loading={loading}
          submitLabel="Create account"
          onVerify={verify}
          onResend={requestCode}
        />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Create an account"
      subtitle="Book appointments and keep your visit history in one place."
      footer={(
        <p>
          {'Already registered? '}
          <Link to={PATHS.login} state={location.state} className="link">Log in</Link>
        </p>
      )}
    >
      <form onSubmit={submitDetails} noValidate>
        {formError && <div className="alert u-mb-4"><IconInfoCircle size={18} />{formError}</div>}
        <TextField
          label="Full name (as per IC)"
          placeholder="e.g. Aisyah binti Rahman"
          autoComplete="name"
          value={form.name}
          onChange={(e) => set('name')(e.target.value)}
          error={errors.name}
        />
        <PhoneField
          countryCode={form.countryCode}
          onCountryCodeChange={set('countryCode')}
          value={form.phone}
          onChange={set('phone')}
          error={errors.phone}
          hint="We’ll send a code to verify this number."
        />
        <TextField
          label="IC number"
          placeholder="YYMMDD-PB-XXXX"
          inputMode="numeric"
          value={formatICInput(form.icNumber)}
          onChange={(e) => set('icNumber')(e.target.value.replace(/\D/g, '').slice(0, 12))}
          error={errors.icNumber}
        />
        <TextField
          label="Email (optional)"
          type="email"
          placeholder="name@example.com"
          autoComplete="email"
          value={form.email}
          onChange={(e) => set('email')(e.target.value)}
          error={errors.email}
        />
        <PasswordField
          label="Password"
          placeholder="At least 8 characters"
          autoComplete="new-password"
          value={form.password}
          onChange={(e) => set('password')(e.target.value)}
          error={errors.password}
        />
        <Checkbox
          className="u-mt-6"
          label="I agree to the Terms and Privacy Policy"
          checked={form.agree}
          onChange={(e) => set('agree')(e.target.checked)}
        />
        {errors.agree && <p className="field__error">{errors.agree}</p>}
        <Button type="submit" block className="u-mt-6" loading={loading}>Continue</Button>
      </form>
    </AuthLayout>
  );
}
